import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Dashboard.css';
import './DataPage.css';

import { API_URL } from '../config';

const styles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
};

function Dashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const getDateStr = (date) => date.toISOString().split('T')[0];
  const hojeStr = getDateStr(new Date());
  const ontemDate = new Date();
  ontemDate.setDate(ontemDate.getDate() - 1);
  const ontemStr = getDateStr(ontemDate);

  const [diaSelecionado, setDiaSelecionado] = useState('hoje');

  const dataDoDia = diaSelecionado === 'hoje' ? hojeStr : ontemStr;

  useEffect(() => {
    fetchDashboardData();
  }, [diaSelecionado]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('dataInicio', dataDoDia);
      params.append('dataFim', dataDoDia);

      const analyticsRes = await axios.get(`${API_URL}/dashboard/analytics?${params.toString()}`);
      setAnalytics(analyticsRes.data);
      setStats(analyticsRes.data.stats);
    } catch (err) {
      console.error('Erro ao buscar dados do dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (!value) return '0';
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  if (loading) {
    return <div className="loading">Carregando dashboard...</div>;
  }

  if (!analytics || !stats) {
    return <div className="loading">Erro ao carregar dados</div>;
  }

  return (
    <div className="dashboard-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>📊</span>
          <div>
            <h1 style={styles.pageTitle}>Dashboard</h1>
            <p style={styles.pageSubtitle}>Resumo operacional do laboratório</p>
          </div>
        </div>
      </div>

      {/* Filtro de Dia */}
      <div className="dashboard-filter">
        <div className="filter-label">Exibindo dados de:</div>
        <div className="filter-day-toggle">
          <button
            className={`day-btn ${diaSelecionado === 'hoje' ? 'active' : ''}`}
            onClick={() => setDiaSelecionado('hoje')}
          >
            Hoje
          </button>
          <button
            className={`day-btn ${diaSelecionado === 'ontem' ? 'active' : ''}`}
            onClick={() => setDiaSelecionado('ontem')}
          >
            Ontem
          </button>
        </div>
        <span className="filter-date-display">{dataDoDia.split('-').reverse().join('/')}</span>
      </div>

      {/* Cards de Estatísticas do Dia */}
      <div className="compact-stats">
        <div className="compact-stat-card blue" onClick={() => navigate('/atendimentos')} style={{ cursor: 'pointer' }}>
          <div className="compact-stat-value">{formatNumber(stats.totalAtendimentos)}</div>
          <div className="compact-stat-label">Atendimentos</div>
        </div>
        <div className="compact-stat-card green" onClick={() => navigate('/pacientes')} style={{ cursor: 'pointer' }}>
          <div className="compact-stat-value">{formatNumber(stats.totalPacientes)}</div>
          <div className="compact-stat-label">Pacientes</div>
        </div>
        <div className="compact-stat-card orange" onClick={() => navigate('/exames')} style={{ cursor: 'pointer' }}>
          <div className="compact-stat-value">{formatNumber(stats.totalExamesSolicitados)}</div>
          <div className="compact-stat-label">Exames Solicitados</div>
        </div>
        <div className="compact-stat-card teal" onClick={() => navigate('/coletas-domiciliares')} style={{ cursor: 'pointer' }}>
          <div className="compact-stat-value">{formatNumber(stats.totalColetasDomiciliares)}</div>
          <div className="compact-stat-label">Coletas Domiciliares</div>
        </div>
      </div>

      {/* Gráficos e Visualizações */}
      <div className="charts-grid">

        {/* Distribuição por Sexo */}
        <div className="chart-card">
          <h3>👤 Distribuição por Sexo</h3>
          <div className="chart-content">
            {analytics.distribuicaoSexo?.map((item, index) => {
              const total = analytics.distribuicaoSexo.reduce((sum, i) => sum + i.total, 0);
              const percentage = ((item.total / total) * 100).toFixed(1);
              return (
                <div key={index} className="chart-bar-item">
                  <div className="chart-bar-label">
                    <span>{item.SEXO || 'Não informado'}</span>
                    <span className="chart-bar-value">{formatNumber(item.total)} ({percentage}%)</span>
                  </div>
                  <div className="chart-bar-bg">
                    <div
                      className="chart-bar-fill"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: index === 0 ? '#3b82f6' : index === 1 ? '#ec4899' : '#6b7280'
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Exames por Situação */}
        {analytics.examesPorSituacao && analytics.examesPorSituacao.length > 0 && (
          <div className="chart-card">
            <h3>🧪 Exames por Situacao</h3>
            <div className="chart-content">
              {analytics.examesPorSituacao.map((item, index) => {
                const total = analytics.examesPorSituacao.reduce((sum, i) => sum + i.total, 0);
                const percentage = ((item.total / total) * 100).toFixed(1);
                const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];
                return (
                  <div key={index} className="chart-bar-item">
                    <div className="chart-bar-label">
                      <span>{item.situacao}</span>
                      <span className="chart-bar-value">{formatNumber(item.total)} ({percentage}%)</span>
                    </div>
                    <div className="chart-bar-bg">
                      <div
                        className="chart-bar-fill"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: colors[index % colors.length]
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Coletas Domiciliares por Usuário */}
        {analytics.coletasDomiciliaresPorUsuario && analytics.coletasDomiciliaresPorUsuario.length > 0 && (
          <div className="chart-card">
            <h3>👤 Top 10 Usuários - Coletas Domiciliares</h3>
            <div className="chart-content">
              {analytics.coletasDomiciliaresPorUsuario.map((item, index) => {
                const max = Math.max(...analytics.coletasDomiciliaresPorUsuario.map(i => i.total_coletas));
                const percentage = ((item.total_coletas / max) * 100).toFixed(1);
                const colors = ['#14b8a6', '#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#06b6d4', '#84cc16'];
                return (
                  <div key={index} className="chart-bar-item">
                    <div className="chart-bar-label">
                      <span>{item.usuario}</span>
                      <span className="chart-bar-value">{formatNumber(item.total_coletas)} coletas | {formatNumber(item.total_pacientes)} pacientes</span>
                    </div>
                    <div className="chart-bar-bg">
                      <div
                        className="chart-bar-fill"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: colors[index % colors.length]
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Coletas Domiciliares por Convênio */}
        {analytics.coletasDomiciliaresPorConvenio && analytics.coletasDomiciliaresPorConvenio.length > 0 && (
          <div className="chart-card">
            <h3>🏥 Coletas Domiciliares por Convênio</h3>
            <div className="chart-content">
              {analytics.coletasDomiciliaresPorConvenio.map((item, index) => {
                const total = analytics.coletasDomiciliaresPorConvenio.reduce((sum, i) => sum + i.total, 0);
                const percentage = ((item.total / total) * 100).toFixed(1);
                const colors = ['#06b6d4', '#14b8a6', '#10b981', '#84cc16', '#f59e0b', '#f97316', '#ef4444', '#ec4899', '#8b5cf6', '#3b82f6'];
                return (
                  <div key={index} className="chart-bar-item">
                    <div className="chart-bar-label">
                      <span>{item.convenio}</span>
                      <span className="chart-bar-value">{formatNumber(item.total)} ({percentage}%)</span>
                    </div>
                    <div className="chart-bar-bg">
                      <div
                        className="chart-bar-fill"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: colors[index % colors.length]
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Dashboard;
